import React, { useEffect, useState } from 'react';
import { firestore, storage } from '../Firebase';
import toast, { Toaster } from 'react-hot-toast';
import {
  Col,
  Container,
  Row,
  OverlayTrigger,
  Tooltip,
  Modal,
  Button,
} from 'react-bootstrap';
import HeaderAdmin from './HeaderAdmin';
import { Link } from 'react-router-dom';
import DropzoneLogo from './DropzoneLogo';
import DropzoneBanner from './DropzoneBanner';
import DropzoneBG from './DropzoneBG';
import DropzoneLowerImage from './DropzoneLowerImage';
import { BeatLoader } from 'react-spinners';
import AddVideo from './AddVideo';
import { useTitle } from '../context/TitleContext';
import { uploadImageToStorage } from '../utils/storageUpload';
import { shouldShowQuestionManagementControls } from '../utils/questions';

const Settings = () => {
  const { setPageTitle } = useTitle();
  const [features, setFeatures] = useState({});
  const [videos, setVideos] = useState([]);
  const [pages, setPages] = useState([]);
  const [customization, setCustomization] = useState({});
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showPageModal, setShowPageModal] = useState(false);
  const [newPageTitle, setNewPageTitle] = useState('');

  useEffect(() => {
    // Prepare state objects outside the snapshot callback
    let featuresData = {};
    let customizationData = {};

    // Combine all settings listeners into a single batch listener
    const unsubscribeSettings = firestore.collection('settings').onSnapshot(
      (snapshot) => {
        // Process all documents first before updating state
        snapshot.docs.forEach((doc) => {
          if (doc.id === 'features' && doc.data()) {
            featuresData = doc.data();
          } else if (doc.id === 'customization' && doc.data()) {
            customizationData = doc.data();

            // Update page title if needed
            if (customizationData.pageTitle) {
              document.title = customizationData.pageTitle;
            }
          }
        });

        // Batch state updates to reduce re-renders
        setFeatures(featuresData);
        setCustomization(customizationData);
      },
      (error) => {
        console.error('Error listening to settings changes:', error);
        toast.error('Error loading settings. Please refresh the page.');
      }
    );

    // Use a separate listener for videos with throttling
    // This prevents too many updates when there are many videos
    let videosTimeout = null;
    const unsubscribeVideos = firestore.collection('videos').onSnapshot(
      (snapshot) => {
        // Clear any pending updates
        if (videosTimeout) clearTimeout(videosTimeout);

        // Throttle updates to prevent too many re-renders
        videosTimeout = setTimeout(() => {
          const videoData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setVideos(videoData);
        }, 300); // 300ms throttle
      },
      (error) => {
        console.error('Error listening to videos changes:', error);
        toast.error('Error loading videos. Please refresh the page.');
      }
    );

    // Listener for pages
    const unsubscribePages = firestore.collection('pages').onSnapshot(
      (snapshot) => {
        const pageData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setPages(pageData);
      },
      (error) => {
        console.error('Error listening to pages changes:', error);
        toast.error('Error loading pages.');
      }
    );

    return () => {
      unsubscribeSettings();
      unsubscribeVideos();
      unsubscribePages();
      if (videosTimeout) clearTimeout(videosTimeout);
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomization((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFeatures((prevState) => ({
      ...prevState,
      [name]: checked,
    }));
    updateFeatures({ ...features, [name]: checked });
  };

  const updateCustomization = () => {
    setLoading(true);
    firestore
      .collection('settings')
      .doc('customization')
      .set(customization, { merge: true })
      .then(() => {
        if (customization.pageTitle) {
          setPageTitle(customization.pageTitle);
        }
        toast.success('Settings updated successfully!');
      })
      .catch((error) => {
        toast.error('Error updating settings: ' + error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const updateFeatures = (updatedFeatures) => {
    setLoading(true);
    firestore
      .collection('settings')
      .doc('features')
      .set(updatedFeatures, { merge: true })
      .then(() => {
        toast.success('Settings updated successfully!');
      })
      .catch((error) => {
        toast.error('Error updating settings: ' + error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const uploadCustomizationImage = async ({ name, uri }, field, successMessage) => {
    if (!name || !uri) return;

    setLoading(true);
    try {
      const downloadURL = await uploadImageToStorage(storage, uri);
      await firestore
        .collection('settings')
        .doc('customization')
        .set({ [field]: downloadURL }, { merge: true });
      toast.success(successMessage);
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(
        'An error occurred while uploading the file. Please try again.'
      );
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const onLogoDropHandler = (image) =>
    uploadCustomizationImage(image, 'logoPath', 'Logo image uploaded successfully');

  const onBannerDropHandler = (image) =>
    uploadCustomizationImage(
      image,
      'bannerPath',
      'Banner image uploaded successfully'
    );

  const onBGDropHandler = (image) =>
    uploadCustomizationImage(
      image,
      'bgPath',
      'Background image uploaded successfully'
    );

  const onLowerImageDropHandler = (image) =>
    uploadCustomizationImage(
      image,
      'lowerImagePath',
      'Lower login image uploaded successfully'
    );

  const handleShowModal = () => {
    setShowModal(true);
  };
  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleAddPage = async () => {
    if (!newPageTitle.trim()) {
      toast.error('Page title cannot be empty');
      return;
    }
    setLoading(true);
    try {
      await firestore.collection('pages').add({
        title: newPageTitle,
        createdAt: new Date(),
      });
      toast.success('Page added successfully');
      setNewPageTitle('');
      setShowPageModal(false);
    } catch (error) {
      console.error('Error adding page:', error);
      toast.error('Error adding page: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePage = (id) => {
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this page?'
    );

    if (confirmDelete) {
      firestore
        .collection('pages')
        .doc(id)
        .delete()
        .then(() => {
          toast.success('Page successfully deleted!');
        })
        .catch((error) => {
          toast.error('Error deleting page: ' + error.message);
        });
    }
  };

  const handleDeleteVideo = (id) => {
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this video?'
    );

    if (confirmDelete) {
      firestore
        .collection('videos')
        .doc(id)
        .delete()
        .then(() => {
          toast.success('Video successfully deleted!');
        })
        .catch((error) => {
          toast.error('Error deleting video: ', error);
        });
    }
  };

  return (
    <Container fluid>
      <Row>
        <HeaderAdmin />
        <Toaster />
        <Modal show={showModal} onHide={handleCloseModal}>
          <Modal.Header closeButton>
            <div className="h4">Add Video</div>
          </Modal.Header>
          <Modal.Body>
            <AddVideo toggleModal={handleCloseModal} />
          </Modal.Body>
        </Modal>
        <Modal show={showPageModal} onHide={() => setShowPageModal(false)}>
          <Modal.Header closeButton>
            <div className="h4">Add Page</div>
          </Modal.Header>
          <Modal.Body>
            <div className="form-group">
              <label htmlFor="pageTitleInput">Page Title</label>
              <input
                id="pageTitleInput"
                type="text"
                className="form-control"
                value={newPageTitle}
                onChange={(e) => setNewPageTitle(e.target.value)}
                placeholder="Enter page title"
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPageModal(false)}>
              Close
            </Button>
            <Button variant="primary" onClick={handleAddPage}>
              Add Page
            </Button>
          </Modal.Footer>
        </Modal>
        {loading && (
          <div className="overlay">
            <BeatLoader
              color="#36d7b7"
              size={15}
              loading={loading}
              aria-label="Loading Spinner"
              data-testid="loader"
            />
          </div>
        )}
      </Row>
      <Row>
        <Col>
          <div className="h1 mt-1 mb-2">Settings</div>
        </Col>
      </Row>
      <Row>
        <Col className="pop3d">
          <h4 className="mt-2">Features:</h4>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasEventPasscode"
                  type="checkbox"
                  checked={features.hasEventPasscode || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={
                    <Tooltip>
                      When Event Passcode is ON, users will need to know the
                      passcode to access the site. If it's off, they just need
                      to enter their name, and other info below.
                    </Tooltip>
                  }
                >
                  <label htmlFor="hasEventPasscode">Has Event Passcode *</label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasFirstLastNames"
                  type="checkbox"
                  checked={features.hasFirstLastNames || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={
                    <Tooltip>
                      This will separate first names and last names in the
                      access/login page.
                    </Tooltip>
                  }
                >
                  <label htmlFor="hasFirstLastNames">
                    Has First/Last Names *
                  </label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasEmailField"
                  type="checkbox"
                  checked={features.hasEmailField || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={
                    <Tooltip>
                      When Need Email is ON, users will be asked to provide
                      their email address to enter the site.
                    </Tooltip>
                  }
                >
                  <label htmlFor="hasEmailField">Has Email Field *</label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasLocationField"
                  type="checkbox"
                  checked={features.hasLocationField || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={
                    <Tooltip>
                      When Need Location is ON, users will be asked to provide
                      their location to enter the site.
                    </Tooltip>
                  }
                >
                  <label htmlFor="hasLocationField">Has Location Field *</label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasBackgroundImage"
                  type="checkbox"
                  checked={features.hasBackgroundImage || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={
                    <Tooltip>
                      When Has Background Image is ON, it enables the background
                      image. When it's off, it shows a gradient background that
                      you can set colors for.
                    </Tooltip>
                  }
                >
                  <label htmlFor="hasBackgroundImage">
                    Has Background Image *
                  </label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasLoginUniqueBGHex"
                  type="checkbox"
                  checked={features.hasLoginUniqueBGHex || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={
                    <Tooltip>
                      When Has Unique Login BG Hex Colors is ON, the background of the login
                      page will have unique hex colors, separate from the inner pages.
                      When it's off, it shows the same gradient background as the inner pages.
                      Set the hex colors in the customization section.
                    </Tooltip>
                  }
                >
                  <label htmlFor="hasLoginUniqueBGHex">
                    Has Unique Login BG Hex Colors *
                  </label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasLoginSubtitle"
                  type="checkbox"
                  checked={features.hasLoginSubtitle || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={
                    <Tooltip>
                      When Login Subtitle is ON, it shows the subtitle on the login page.
                    </Tooltip>
                  }
                >
                  <label htmlFor="hasLoginSubtitle">Has Login Subtitle *</label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasLightSubtitle"
                  type="checkbox"
                  checked={features.hasLightSubtitle || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={
                    <Tooltip>
                      When Light Subtitle is ON, it makes the subtitles the
                      light color. It's useful when your background image is
                      dark.
                    </Tooltip>
                  }
                >
                  <label htmlFor="hasLightSubtitle">Has Light Subtitle *</label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasLoginLogo"
                  type="checkbox"
                  checked={features.hasLoginLogo || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={
                    <Tooltip>
                      When Has Login Logo is ON, it enables the login logo on
                      the site. Currently only William Zauscher can upload the
                      logo for you.
                    </Tooltip>
                  }
                >
                  <label htmlFor="hasLoginLogo">Has Login Logo *</label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="has300pxLogo"
                  type="checkbox"
                  checked={features.has300pxLogo || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={
                    <Tooltip>
                      When Has 300px Logo is ON, it makes the login logo max out at 300px. This prevents a tall logo from taking over the page at width = 100% of the div.
                    </Tooltip>
                  }
                >
                  <label htmlFor="has300pxLogo">Has 300px Logo *</label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasLoginLowerImage"
                  type="checkbox"
                  checked={features.hasLoginLowerImage || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={
                    <Tooltip>
                      When Has Lower Login Image is ON, it enables an image below the login form. This image must be uploaded in the customization section.
                    </Tooltip>
                  }
                >
                  <label htmlFor="hasLoginLowerImage">Has Lower Login Image *</label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasBannerLogo"
                  type="checkbox"
                  checked={features.hasBannerLogo || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={
                    <Tooltip>
                      When Has Banner Logo is ON, it enables the banner logo on
                      the site. Currently only William Zauscher can upload the
                      logo for you.
                    </Tooltip>
                  }
                >
                  <label htmlFor="hasBannerLogo">Has Banner Logo *</label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasCookieNotification"
                  type="checkbox"
                  checked={features.hasCookieNotification || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={
                    <Tooltip>
                      Notify people of cookies in the access/login page
                    </Tooltip>
                  }
                >
                  <label htmlFor="hasCookieNotification">
                    Has Cookie Notification *
                  </label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasQuestionBox"
                  type="checkbox"
                  checked={features.hasQuestionBox || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={
                    <Tooltip>
                      When Has Question Box is ON, it lets users type in
                      questions. Only you see the questions.
                    </Tooltip>
                  }
                >
                  <label htmlFor="hasQuestionBox">Has Question Box *</label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasAnonymousQuestions"
                  type="checkbox"
                  checked={features.hasAnonymousQuestions || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={
                    <Tooltip>
                      Store new questions without a name or user ID and label
                      them Anonymous in the admin view and CSV exports.
                    </Tooltip>
                  }
                >
                  <label htmlFor="hasAnonymousQuestions">
                    Has Anonymous Questions *
                  </label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasQuestionManagementControls"
                  type="checkbox"
                  checked={shouldShowQuestionManagementControls(features)}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={
                    <Tooltip>
                      Show Download CSV and Clear Questions above the admin
                      question monitor. Turn this off to hide both controls.
                    </Tooltip>
                  }
                >
                  <label htmlFor="hasQuestionManagementControls">
                    Has Question Management Controls *
                  </label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasChat"
                  type="checkbox"
                  checked={features.hasChat || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={
                    <Tooltip>
                      When Enable Chat is on, it enables the chat feature. This
                      is different from turning the chat on or off above, which
                      only controls the posts that users see.
                    </Tooltip>
                  }
                >
                  <label htmlFor="hasChat">Has Chat *</label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasDynamicBox"
                  type="checkbox"
                  checked={features.hasDynamicBox || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={
                    <Tooltip>
                      When Dynamic Box is ON, it enables the dynamic box
                      functionality and you will be able to control it
                      dynamically.
                    </Tooltip>
                  }
                >
                  <label htmlFor="hasDynamicBox">Has Dynamic Box *</label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>

          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasDarkModeOn"
                  type="checkbox"
                  checked={features.hasDarkModeOn || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={
                    <Tooltip>
                      When Has Dark Mode is ON, it changes the white backgrounds
                      to dark backgrounds. It's useful when your background
                      image is light-colored.
                    </Tooltip>
                  }
                >
                  <label htmlFor="hasDarkModeOn">Has Dark Mode On *</label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>

          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasEmojiReactions"
                  type="checkbox"
                  checked={features.hasEmojiReactions || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={<Tooltip>Self-explanatory :)</Tooltip>}
                >
                  <label htmlFor="hasEmojiReactions">
                    Has Emoji Reactions *
                  </label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>

          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasRegistration"
                  type="checkbox"
                  checked={features.hasRegistration || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={
                    <Tooltip>
                      When registration is ON, users need to register with this
                      site and get their own password. When it is off, there is
                      no need to have your own account on this site.
                    </Tooltip>
                  }
                >
                  <label htmlFor="hasRegistration">Has Registration *</label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasRegistrationCode"
                  type="checkbox"
                  checked={features.hasRegistrationCode || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={
                    <Tooltip>
                      When registration code is ON, users must know the code to
                      be able to register with this site. Registration Code =
                      Event Passcode
                    </Tooltip>
                  }
                >
                  <label htmlFor="hasRegistrationCode">
                    Has Registration Code *
                  </label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasPreRegistration"
                  type="checkbox"
                  checked={features.hasPreRegistration || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={
                    <Tooltip>
                      When Pre-Registration is ON, it users aren't allowed to
                      register. All registration is pre-submitted via csv.
                    </Tooltip>
                  }
                >
                  <label htmlFor="hasPreRegistration">
                    Has Pre-Registration *
                  </label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasOneTimeLogin"
                  type="checkbox"
                  checked={features.hasOneTimeLogin || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={
                    <Tooltip>
                      When One Time Login is ON, pre-registered users will only
                      be able to log in once. It's to discourage credential
                      sharing for on-demand.
                    </Tooltip>
                  }
                >
                  <label htmlFor="hasOneTimeLogin">Has One Time Login *</label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasSessionChecker"
                  type="checkbox"
                  checked={features.hasSessionChecker || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={
                    <Tooltip>
                      When Session Checker is ON, it makes there only be a
                      single session at a time (with registration). It's to
                      discourage credential sharing for live.
                    </Tooltip>
                  }
                >
                  <label htmlFor="hasSessionChecker">Has Session Checker *</label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasConferenceModeOn"
                  type="checkbox"
                  checked={features.hasConferenceModeOn || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={
                    <Tooltip>
                      When Conference Mode is ON, multiple videos will be
                      enabled on the platform. A lobby page shows up when you
                      log in, with the selection of videos.
                    </Tooltip>
                  }
                >
                  <label htmlFor="hasConferenceModeOn">
                    Has Conference Mode On *
                  </label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasAccountPage"
                  type="checkbox"
                  checked={features.hasAccountPage || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={
                    <Tooltip>
                      When Show Account Page is ON, it lets users change their
                      passwords.
                    </Tooltip>
                  }
                >
                  <label htmlFor="hasAccountPage">Has Account Page</label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasUserList"
                  type="checkbox"
                  checked={features.hasUserList || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={
                    <Tooltip>
                      When Has User List is on, it lets users see who else is
                      concurrently viewing the stream with them. In other words,
                      it can let them know that there are people to chat with.
                    </Tooltip>
                  }
                >
                  <label htmlFor="hasUserList">Has User List</label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasLiveCaptioning"
                  type="checkbox"
                  checked={features.hasLiveCaptioning || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={
                    <Tooltip>
                      When Live Captioning is on, there is a live captioning box
                      that appears in the sidebar, above the chat. The box
                      toggles on and off and is off by default. There needs to
                      be a live captioner service set up for this to work.
                    </Tooltip>
                  }
                >
                  <label htmlFor="hasLiveCaptioning">Has Live Captioning</label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasLobbySidebar"
                  type="checkbox"
                  checked={features.hasLobbySidebar || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={
                    <Tooltip>
                      Show a sidebar in the lobby page, with its own chat
                    </Tooltip>
                  }
                >
                  <label htmlFor="hasLobbySidebar">Has Lobby Sidebar</label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>

          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasPlayerFooter"
                  type="checkbox"
                  checked={features.hasPlayerFooter || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={<Tooltip>Show player footer</Tooltip>}
                >
                  <label htmlFor="hasPlayerFooter">Has Player Footer</label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasAudioOnlyVersion"
                  type="checkbox"
                  checked={features.hasAudioOnlyVersion || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={<Tooltip>Only for DJO</Tooltip>}
                >
                  <label htmlFor="hasAudioOnlyVersion">
                    Has Audio Only Version
                  </label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasMessageSelection"
                  type="checkbox"
                  checked={features.hasMessageSelection || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={<Tooltip>Enable selecting messages on chat</Tooltip>}
                >
                  <label htmlFor="hasMessageSelection">
                    Has Message Selection
                  </label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasAdminVideoPlayer"
                  type="checkbox"
                  checked={features.hasAdminVideoPlayer || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={<Tooltip>Show video player in admin pages</Tooltip>}
                >
                  <label htmlFor="hasAdminVideoPlayer">
                    Has Admin Video Player
                  </label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  name="hasHiddenPosts"
                  type="checkbox"
                  checked={features.hasHiddenPosts || false}
                  onChange={handleCheckboxChange}
                ></input>
                <OverlayTrigger
                  placement={'right'}
                  overlay={
                    <Tooltip>
                      Show the chat messages that were hidden in the chat
                      control page.
                    </Tooltip>
                  }
                >
                  <label htmlFor="hasHiddenPosts">Has Hidden Posts</label>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>
        </Col>
        <Col className="pop3d">
          <h4 className="mt-2">Videos:</h4>
          <OverlayTrigger
            placement={'left'}
            overlay={
              <Tooltip>
                When Conference Mode is OFF, this video will be displayed.
              </Tooltip>
            }
          >
            <div className="form-floating mb-3">
              <select
                className="form-control"
                name="single-title"
                placeholder="single-title"
                onChange={(e) => {
                  const selectedVideo = videos.find(
                    (v) => v.id === e.target.value
                  );
                  const newCustomization = selectedVideo
                    ? {
                        ...customization,
                        singleVideoTitle: selectedVideo.title,
                        singleVideoId: selectedVideo.id,
                      }
                    : {
                        ...customization,
                        singleVideoTitle: '',
                        singleVideoId: '',
                      };
                  setCustomization(newCustomization);

                  setLoading(true);
                  firestore
                    .collection('settings')
                    .doc('customization')
                    .set(newCustomization, { merge: true })
                    .then(() => {
                      if (newCustomization.pageTitle) {
                        setPageTitle(newCustomization.pageTitle);
                      }
                      toast.success('Settings updated successfully!');
                    })
                    .catch((error) => {
                      toast.error('Error updating settings: ' + error.message);
                    })
                    .finally(() => {
                      setLoading(false);
                    });
                }}
                value={customization.singleVideoId || ''}
              >
                <option value="">Select a video</option>
                {videos.map((video) => (
                  <option key={video.id} value={video.id}>
                    {video.title || 'Untitled Video'} ({video.id})
                  </option>
                ))}
              </select>
              <label htmlFor="single-title">Single Video</label>
            </div>
          </OverlayTrigger>
          <Row>
            <Col>
              <Button
                className="btn btn-primary mb-2"
                name="addVideo"
                onClick={handleShowModal}
              >
                Add Video
              </Button>
            </Col>
          </Row>
          <Row>
            <Col>
              <table width="100%">
                <tbody>
                  {videos.map((video, index) => (
                    <tr
                      key={video.id}
                      style={{
                        backgroundColor: index % 2 === 0 ? '#f2f2f2' : 'white',
                      }}
                    >
                      <td className="col-3">
                        <img
                          className="m-2"
                          src={video.thumbnail}
                          width={100}
                          alt="Video thumbnail"
                        />
                      </td>
                      <td className="col-6">
                        <Link to={`/admin/manage/${video.id}`}>
                          {video.text || video.title || 'Untitled Video'}
                        </Link>
                      </td>
                      <td className="col-3">
                        <Button
                          className="btn btn-danger"
                          onClick={() => handleDeleteVideo(video.id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Col>
          </Row>
          <h4 className="mt-4">Pages:</h4>
          <Row>
            <Col>
              <Button
                className="btn btn-primary mb-2"
                onClick={() => setShowPageModal(true)}
              >
                Add Page
              </Button>
            </Col>
          </Row>
          <Row>
            <Col>
              <table width="100%">
                <tbody>
                  {pages.map((page, index) => (
                    <tr
                      key={page.id}
                      style={{
                        backgroundColor: index % 2 === 0 ? '#f2f2f2' : 'white',
                      }}
                    >
                      <td className="col-9 p-2">
                        <Link to={`/admin/page-edit/${page.id}`}>
                          {page.title || 'Untitled Page'}
                        </Link>
                      </td>
                      <td className="col-3">
                        <Button
                          className="btn btn-danger"
                          onClick={() => handleDeletePage(page.id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Col>
          </Row>
        </Col>
        <Col className="pop3d">
          <h4 className="mt-2">Customization:</h4>
          <OverlayTrigger
            placement={'left'}
            overlay={
              <Tooltip>
                This sets the title of the site, that gets displayed when the
                logo is off or as the alt text to the logo when it is on.
              </Tooltip>
            }
          >
            <div className="input-group mb-3">
              <div className="form-floating form-floating-group flex-grow-1">
                <input
                  className="form-control conf-option-field"
                  name="pageTitle"
                  type="text"
                  value={customization.pageTitle || ''}
                  onChange={handleInputChange}
                ></input>
                <label className="form-label text-start" htmlFor="pageTitle">
                  Page Title
                </label>
              </div>
              <button
                className="input-group-text set-btn btn btn-primary"
                name="changePageTitle"
                onClick={updateCustomization}
              >
                Set
              </button>
            </div>
          </OverlayTrigger>
          <OverlayTrigger
            placement={'left'}
            overlay={
              <Tooltip>
                This sets the subtitle of the site, that gets displayed on the
                top left of the inner page(s), below the logo.
              </Tooltip>
            }
          >
            <div className="input-group mb-3">
              <div className="form-floating form-floating-group flex-grow-1">
                <input
                  className="form-control conf-option-field"
                  name="subtitle"
                  type="text"
                  value={customization.subtitle || ''}
                  onChange={handleInputChange}
                ></input>
                <label className="form-label text-start" htmlFor="subtitle">
                  Subtitle
                </label>
              </div>
              <button
                className="input-group-text set-btn btn btn-primary"
                name="changeSubtitle"
                onClick={updateCustomization}
              >
                Set
              </button>
            </div>
          </OverlayTrigger>
          <OverlayTrigger
            placement={'left'}
            overlay={
              <Tooltip>
                This lets you customize the message on the access page.
              </Tooltip>
            }
          >
            <div className="input-group mb-3">
              <div className="form-floating form-floating-group flex-grow-1">
                <input
                  className="form-control conf-option-field"
                  name="accessMessage"
                  type="text"
                  value={customization.accessMessage || ''}
                  onChange={handleInputChange}
                ></input>
                <label
                  className="form-label text-start"
                  htmlFor="accessMessage"
                >
                  Access Message
                </label>
              </div>
              <button
                className="input-group-text set-btn btn btn-primary"
                name="changeAccessMessage"
                onClick={updateCustomization}
              >
                Set
              </button>
            </div>
          </OverlayTrigger>
          <OverlayTrigger
            placement={'left'}
            overlay={
              <Tooltip>
                Add one help/troubleshooting item per line. When this is empty,
                the public help icon is hidden.
              </Tooltip>
            }
          >
            <div className="input-group mb-3">
              <div className="form-floating form-floating-group flex-grow-1">
                <textarea
                  id="helperItems"
                  className="form-control conf-option-field"
                  name="helperItems"
                  value={customization.helperItems || ''}
                  onChange={handleInputChange}
                  style={{ height: '120px' }}
                ></textarea>
                <label className="form-label text-start" htmlFor="helperItems">
                  Helper Items (one per line)
                </label>
              </div>
              <button
                className="input-group-text set-btn btn btn-primary"
                name="changeHelperItems"
                onClick={updateCustomization}
              >
                Set
              </button>
            </div>
          </OverlayTrigger>
          <OverlayTrigger
            placement={'left'}
            overlay={
              <Tooltip>
                This lets you customize the message on the login button.
              </Tooltip>
            }
          >
            <div className="input-group mb-3">
              <div className="form-floating form-floating-group flex-grow-1">
                <input
                  className="form-control conf-option-field"
                  name="loginButtonMessage"
                  type="text"
                  value={customization.loginButtonMessage || ''}
                  onChange={handleInputChange}
                ></input>
                <label
                  className="form-label text-start"
                  htmlFor="loginButtonMessage"
                >
                  Login Button Message
                </label>
              </div>
              <button
                className="input-group-text set-btn btn btn-primary"
                name="changeloginButtonMessage"
                onClick={updateCustomization}
              >
                Set
              </button>
            </div>
          </OverlayTrigger>
          <OverlayTrigger
            placement={'left'}
            overlay={
              <Tooltip>
                This is the passcode to access the site. When registration is
                on, this is the Registration Code.
              </Tooltip>
            }
          >
            <div className="input-group mb-3">
              <div className="form-floating form-floating-group flex-grow-1">
                <input
                  className="form-control conf-option-field"
                  name="eventPasscode"
                  type="text"
                  value={customization.eventPasscode || ''}
                  onChange={handleInputChange}
                ></input>
                <label
                  className="form-label text-start"
                  htmlFor="eventPasscode"
                >
                  Event Passcode
                </label>
              </div>
              <button
                className="input-group-text set-btn btn btn-primary"
                name="changeEventPasscode"
                onClick={updateCustomization}
              >
                Set
              </button>
            </div>
          </OverlayTrigger>
          <OverlayTrigger
            placement={'left'}
            overlay={
              <Tooltip>
                This lets you customize the message displayed in the chat entry
                field.
              </Tooltip>
            }
          >
            <div className="input-group mb-3">
              <div className="form-floating form-floating-group flex-grow-1">
                <input
                  className="form-control conf-option-field"
                  name="chatInvMessage"
                  type="text"
                  value={customization.chatInvMessage || ''}
                  onChange={handleInputChange}
                ></input>
                <label
                  className="form-label text-start"
                  htmlFor="chatInvMessage"
                >
                  Chat Invitation Message
                </label>
              </div>
              <button
                className="input-group-text set-btn btn btn-primary"
                name="changeChatInvMessage"
                onClick={updateCustomization}
              >
                Set
              </button>
            </div>
          </OverlayTrigger>
          <OverlayTrigger
            placement={'left'}
            overlay={
              <Tooltip>Show this message when the chat is turned off.</Tooltip>
            }
          >
            <div className="input-group mb-3">
              <div className="form-floating form-floating-group flex-grow-1">
                <input
                  className="form-control conf-option-field"
                  name="chatDisableMessage"
                  type="text"
                  value={customization.chatDisableMessage || ''}
                  onChange={handleInputChange}
                ></input>
                <label
                  className="form-label text-start"
                  htmlFor="chatDisableMessage"
                >
                  Chat Disable Message
                </label>
              </div>
              <button
                className="input-group-text set-btn btn btn-primary"
                name="changeChatDisableMessage"
                onClick={updateCustomization}
              >
                Set
              </button>
            </div>
          </OverlayTrigger>
          <OverlayTrigger
            placement={'left'}
            overlay={
              <Tooltip>
                This title shows up when you have the Dynamic Box enabled and
                are displaying something on it. If it is set to empty, the title
                will disappear.
              </Tooltip>
            }
          >
            <div className="input-group mb-3">
              <div className="form-floating form-floating-group flex-grow-1">
                <input
                  className="form-control conf-option-field"
                  name="dynamicBoxTitle"
                  type="text"
                  value={customization.dynamicBoxTitle || ''}
                  onChange={handleInputChange}
                ></input>
                <label
                  className="form-label text-start"
                  htmlFor="dynamicBoxTitle"
                >
                  Dynamic Box Title
                </label>
              </div>
              <button
                className="input-group-text set-btn btn btn-primary"
                name="changeDynamicBoxTitle"
                onClick={updateCustomization}
              >
                Set
              </button>
            </div>
          </OverlayTrigger>
          <OverlayTrigger
            placement={'left'}
            overlay={
              <Tooltip>This footer appears below the player. Use HTML.</Tooltip>
            }
          >
            <div className="input-group mb-3">
              <div className="form-floating form-floating-group flex-grow-1">
                <input
                  className="form-control conf-option-field"
                  name="playerFooter"
                  type="text"
                  value={customization.playerFooter || ''}
                  onChange={handleInputChange}
                ></input>
                <label className="form-label text-start" htmlFor="playerFooter">
                  Player Footer
                </label>
              </div>
              <button
                className="input-group-text set-btn btn btn-primary"
                name="changePlayerFooter"
                onClick={updateCustomization}
              >
                Set
              </button>
            </div>
          </OverlayTrigger>
          <OverlayTrigger
            placement={'left'}
            overlay={
              <Tooltip>
                These three links show up on the landing page and in the inner
                pages. They let you link to your organization and/or other sites
                for inforrmation or donations, etc. When they are left blank,
                they don't show up.
              </Tooltip>
            }
          >
            <div>
              <div className="input-group mb-3">
                <div className="form-floating form-floating-group flex-grow-1">
                  <input
                    className="form-control conf-option-field"
                    name="linkText1"
                    type="text"
                    value={customization.linkText1 || ''}
                    onChange={handleInputChange}
                  ></input>
                  <label className="form-label text-start" htmlFor="linkText1">
                    External Link 1 Text
                  </label>
                </div>
                <div className="form-floating form-floating-group flex-grow-1">
                  <input
                    className="form-control conf-option-field"
                    name="linkUrl1"
                    type="text"
                    value={customization.linkUrl1 || ''}
                    onChange={handleInputChange}
                  ></input>
                  <label className="form-label text-start" htmlFor="linkUrl1">
                    External Link 1 URL
                  </label>
                </div>
                <button
                  className="input-group-text set-btn btn btn-primary"
                  name="change-link1"
                  onClick={updateCustomization}
                >
                  Set
                </button>
              </div>

              <div className="input-group mb-3">
                <div className="form-floating form-floating-group flex-grow-1">
                  <input
                    className="form-control conf-option-field"
                    name="linkText2"
                    type="text"
                    value={customization.linkText2 || ''}
                    onChange={handleInputChange}
                  ></input>
                  <label className="form-label text-start" htmlFor="linkText2">
                    External Link 2 Text
                  </label>
                </div>
                <div className="form-floating form-floating-group flex-grow-1">
                  <input
                    className="form-control conf-option-field"
                    name="linkUrl2"
                    type="text"
                    value={customization.linkUrl2 || ''}
                    onChange={handleInputChange}
                  ></input>
                  <label className="form-label text-start" htmlFor="linkUrl2">
                    External Link 2 URL
                  </label>
                </div>
                <button
                  className="input-group-text set-btn btn btn-primary"
                  name="change-link2"
                  onClick={updateCustomization}
                >
                  Set
                </button>
              </div>
              <div className="input-group mb-3">
                <div className="form-floating form-floating-group flex-grow-1">
                  <input
                    className="form-control conf-option-field"
                    name="linkText3"
                    type="text"
                    value={customization.linkText3 || ''}
                    onChange={handleInputChange}
                  ></input>
                  <label className="form-label text-start" htmlFor="linkText3">
                    External Link 3 Text
                  </label>
                </div>
                <div className="form-floating form-floating-group flex-grow-1">
                  <input
                    className="form-control conf-option-field"
                    name="linkUrl3"
                    type="text"
                    value={customization.linkUrl3 || ''}
                    onChange={handleInputChange}
                  ></input>
                  <label className="form-label text-start" htmlFor="linkUrl3">
                    External Link 3 URL
                  </label>
                </div>
                <button
                  className="input-group-text set-btn btn btn-primary"
                  name="changePlayerfooter"
                  onClick={updateCustomization}
                >
                  Set
                </button>
              </div>
            </div>
          </OverlayTrigger>
          <OverlayTrigger
            placement={'left'}
            overlay={
              <Tooltip>
                This lets you customize the gradient colors on the background.
                When the background image is turned off, you will see this
                gradient. To get these hex codes, you may need to ask your
                designer.
              </Tooltip>
            }
          >
            <div>
              <div className="input-group mb-3">
                <div className="form-floating form-floating-group flex-grow-1">
                  <input
                    className="form-control conf-option-field"
                    name="hex1"
                    type="text"
                    value={customization.hex1 || ''}
                    onChange={handleInputChange}
                  ></input>
                  <label className="form-label text-start" htmlFor="hex1">
                    Gradient First Color Hex Code (default is #015385)
                  </label>
                </div>
                <button
                  className="input-group-text set-btn btn btn-primary"
                  name="changeHex1"
                  onClick={updateCustomization}
                >
                  Set
                </button>
              </div>
              <div className="input-group mb-3">
                <div className="form-floating form-floating-group flex-grow-1">
                  <input
                    className="form-control conf-option-field"
                    name="hex2"
                    type="text"
                    value={customization.hex2 || ''}
                    onChange={handleInputChange}
                  ></input>
                  <label className="form-label text-start" htmlFor="hex2">
                    Gradient Second Color Hex Code (default is #041527)
                  </label>
                </div>
                <button
                  className="input-group-text set-btn btn btn-primary"
                  name="changeHex2"
                  onClick={updateCustomization}
                >
                  Set
                </button>
              </div>

              {features.hasLoginUniqueBGHex && (
                <>
                  <div className="input-group mb-3">
                    <div className="form-floating form-floating-group flex-grow-1">
                      <input
                        className="form-control conf-option-field"
                        name="loginHex1"
                        type="text"
                        value={customization.loginHex1 || ''}
                        onChange={handleInputChange}
                      ></input>
                      <label className="form-label text-start" htmlFor="loginHex1">
                        Login Gradient First Color Hex Code
                      </label>
                    </div>
                    <button
                      className="input-group-text set-btn btn btn-primary"
                      name="changeLoginHex1"
                      onClick={updateCustomization}
                    >
                      Set
                    </button>
                  </div>
                  <div className="input-group mb-3">
                    <div className="form-floating form-floating-group flex-grow-1">
                      <input
                        className="form-control conf-option-field"
                        name="loginHex2"
                        type="text"
                        value={customization.loginHex2 || ''}
                        onChange={handleInputChange}
                      ></input>
                      <label className="form-label text-start" htmlFor="loginHex2">
                        Login Gradient Second Color Hex Code
                      </label>
                    </div>
                    <button
                      className="input-group-text set-btn btn btn-primary"
                      name="changeLoginHex2"
                      onClick={updateCustomization}
                    >
                      Set
                    </button>
                  </div>
                </>
              )}
            </div>
          </OverlayTrigger>

          <DropzoneLogo
            onLogoDropHandler={onLogoDropHandler}
            logoPath={customization.logoPath}
          />

          <DropzoneBanner
            onBannerDropHandler={onBannerDropHandler}
            bannerPath={customization.bannerPath}
          />

          <DropzoneBG
            onBGDropHandler={onBGDropHandler}
            bgPath={customization.bgPath}
          />

          <DropzoneLowerImage
            onLowerImageDropHandler={onLowerImageDropHandler}
            lowerImagePath={customization.lowerImagePath}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default Settings;
